import React, { useCallback, useEffect, useState } from 'react';
import { Search, ShieldCheck, ShieldOff, Users as UsersIcon, Trash2, Loader2, RefreshCw, UserPlus, Monitor, FileText } from 'lucide-react';
import UserDevicesModal from './UserDevicesModal';
import SharingFlagsCard from './SharingFlagsCard';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABEL = {
  free: 'Free',
  monthly: 'Monthly',
  annual: 'Annual',
  founding: 'Founding',
  comp: 'VIP / Comp',
};

const PLAN_COLOUR = {
  free: 'bg-zinc-800 text-zinc-300',
  monthly: 'bg-emerald-950/60 text-emerald-300 border border-emerald-700/50',
  annual: 'bg-blue-950/60 text-blue-300 border border-blue-700/50',
  founding: 'bg-yellow-950/60 text-yellow-300 border border-yellow-600/50',
};

const fmt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Always Sydney so the Joined dates line up with the signup tiles.
  return d.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric' });
};

const UsersTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [devicesFor, setDevicesFor] = useState(null);
  const [usageId, setUsageId] = useState('');
  const [closedEmail, setClosedEmail] = useState('');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');

  const exportAccounts = async () => {
    const p = new URLSearchParams();
    if (exportFrom) p.set('date_from', exportFrom);
    if (exportTo) p.set('date_to', exportTo);
    try {
      const r = await fetch(`${API}/api/admin/accounts/export?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outlaw-accounts-${exportFrom || 'all'}-${exportTo || 'now'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  };

  const headers = { Authorization: `Bearer ${token}` };

  const downloadUsage = async (u) => {
    setUsageId(u.id);
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/usage/${u.id}/pdf`, { headers });
      if (!r.ok) throw new Error('Could not build the usage record.');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outlaw-usage-${(u.email || u.id).replace(/[^a-z0-9.@-]/gi, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setUsageId('');
    }
  };

  const downloadClosedUsage = async () => {
    const email = closedEmail.trim().toLowerCase();
    if (!email) return;
    setUsageId('closed');
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/usage/by-email/${encodeURIComponent(email)}/pdf`, { headers });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'No record found for that email.');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outlaw-usage-${email.replace(/[^a-z0-9.@-]/gi, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setUsageId('');
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/users/stats`, { headers });
      if (r.ok) setStats(await r.json());
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (plan) params.set('plan', plan);
      const r = await fetch(`${API}/api/admin/users?${params}`, { headers });
      if (!r.ok) throw new Error('Load failed');
      const data = await r.json();
      setRows(data.items || []);
    } catch (e) {
      setError(e.message || 'Could not load accounts.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, q, plan]);

  useEffect(() => { loadStats(); load(); }, [loadStats, load]);

  const toggleComp = async (row) => {
    setBusyId(row.id);
    try {
      const r = await fetch(`${API}/api/admin/users/${row.id}/comp`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ grant: !row.comp_access }),
      });
      if (!r.ok) throw new Error('Update failed');
      await Promise.all([loadStats(), load()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId('');
    }
  };

  const changePlan = async (row, plan) => {
    if (!plan || plan === row.plan) return;
    if (!window.confirm(`Set ${row.email} to ${plan.toUpperCase()}? Use this when a reader pays outside the site (direct debit, cash, contra).`)) return;
    setBusyId(row.id);
    try {
      const r = await fetch(`${API}/api/admin/users/${row.id}/plan`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Plan change failed');
      // Surface whether the notification email actually left the outbox.
      // Resend can silently reject (unverified domain, daily quota) - the
      // admin needs to see that instead of assuming the reader got the note.
      if (plan !== 'free') {
        if (data.notification_email_sent) {
          setError('');
          window.alert(`Tier saved. Notification email sent to ${row.email}.`);
        } else if (data.notification_email_error) {
          window.alert(
            `Tier saved BUT notification email failed to send:\n\n${data.notification_email_error}\n\n` +
            `Fix: verify outlawmedia.com.au at https://resend.com/domains and set RESEND_FROM to a matching address.`
          );
        }
      }
      await Promise.all([loadStats(), load()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId('');
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete account ${row.email}? This can't be undone.`)) return;
    setBusyId(row.id);
    try {
      const r = await fetch(`${API}/api/admin/users/${row.id}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error('Delete failed');
      await Promise.all([loadStats(), load()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId('');
    }
  };

  // ---- Off-site paid-account creation ------------------------------------
  const [createEmail, setCreateEmail] = useState('');
  const [createPlan, setCreatePlan] = useState('monthly');
  const [createSendLink, setCreateSendLink] = useState(true);
  const [creating, setCreating] = useState(false);

  const createPaid = async () => {
    const email = createEmail.trim().toLowerCase();
    if (!email.includes('@')) { setError('Enter a valid email first.'); return; }
    if (!window.confirm(`Create ${email} and grant ${createPlan.toUpperCase()} access instantly?\n\nUse this for readers who paid off-site (direct debit, cash, contra).`)) return;
    setCreating(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/users/create-paid`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, plan: createPlan, send_magic_link: createSendLink }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Create failed.');
      let msg = `Account created + ${createPlan.toUpperCase()} access granted for ${email}.`;
      if (createSendLink) msg += data.magic_link_error
        ? `\n\nMagic link email failed: ${data.magic_link_error}`
        : '\n\nMagic-link email queued so they can sign in.';
      if (!data.upgrade_email_sent && data.upgrade_email_error) {
        msg += `\n\nTier email failed: ${data.upgrade_email_error}`;
      }
      window.alert(msg);
      setCreateEmail('');
      await Promise.all([loadStats(), load()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const StatCard = ({ label, value, sub }) => (
    <div className="border border-zinc-800 rounded-lg bg-zinc-950/70 p-3">
      <p className="text-[10px] text-zinc-500 uppercase tracking-widest">{label}</p>
      <p className="text-white text-2xl font-black leading-none mt-1">{value ?? '-'}</p>
      {sub && <p className="text-zinc-500 text-[10px] mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-users-tab">
      <SharingFlagsCard token={token} onChanged={load} />
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.3em]">Accounts</p>
          <h1 className="text-white text-xl sm:text-2xl font-black flex items-center gap-2"><UsersIcon className="h-5 w-5 sm:h-6 sm:w-6" /> Reader Accounts</h1>
          <p className="text-zinc-500 text-xs sm:text-sm mt-1">Grant free VIP access to friends & family, and track sign-ups.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm('Send a one-time magic-link invite to every newsletter subscriber who doesn\'t have an account yet? Idempotent - safe to re-run.')) return;
              setBusyId('migrate');
              try {
                const r = await fetch(`${API}/api/admin/users/migrate-subscribers`, { method: 'POST', headers });
                const data = await r.json();
                if (!r.ok) throw new Error(data.detail || 'Migrate failed.');
                alert(`Invited ${data.sent} readers (of ${data.planned} pending, ${data.failed} failed).`);
                await Promise.all([loadStats(), load()]);
              } catch (e) { setError(e.message); }
              finally { setBusyId(''); }
            }}
            disabled={busyId === 'migrate'}
            data-testid="users-migrate-subscribers"
            className="inline-flex items-center gap-2 bg-red-950/60 hover:bg-red-900/60 border border-red-700/50 text-red-200 text-[10px] sm:text-xs font-black uppercase tracking-widest px-3 py-2 rounded"
          >
            {busyId === 'migrate' ? 'Inviting…' : <><span className="hidden sm:inline">Invite Newsletter Subscribers</span><span className="sm:hidden">Invite Subs</span></>}
          </button>
          <button
            type="button"
            onClick={() => { loadStats(); load(); }}
            className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-[10px] sm:text-xs font-black uppercase tracking-widest px-3 py-2 rounded"
            data-testid="users-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3" data-testid="users-stats">
        <StatCard label="Total accounts" value={stats?.total} />
        <StatCard label="Free" value={stats?.free} />
        <StatCard label="Monthly" value={stats?.paid_monthly} />
        <StatCard label="Annual" value={stats?.paid_annual} />
        <StatCard label="Founding" value={stats?.founding} />
        <StatCard label="VIP / Comp" value={stats?.comp} />
        <StatCard label={`Signups (today · 7d)`} value={`${stats?.signups_today ?? stats?.signups_24h ?? 0} · ${stats?.signups_7d ?? 0}`} />
      </div>

      {/* Signup export: accounts with Sydney join time + campaign attribution. */}
      <div className="border border-zinc-800 bg-zinc-900/40 rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="accounts-export-card">
        <span className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">
          Export accounts (signup date range, Sydney)
        </span>
        <input
          type="date"
          value={exportFrom}
          onChange={(e) => setExportFrom(e.target.value)}
          data-testid="accounts-export-from"
          className="h-9 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs text-white outline-none focus:border-red-500"
        />
        <span className="text-zinc-600 text-xs">to</span>
        <input
          type="date"
          value={exportTo}
          onChange={(e) => setExportTo(e.target.value)}
          data-testid="accounts-export-to"
          className="h-9 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs text-white outline-none focus:border-red-500"
        />
        <button
          type="button"
          onClick={exportAccounts}
          data-testid="accounts-export-btn"
          className="h-9 px-3 rounded bg-green-600 hover:bg-green-700 text-white text-xs font-black uppercase tracking-widest"
        >
          Download CSV
        </button>
      </div>

      <div
        className="border-2 border-red-600/50 bg-gradient-to-br from-red-950/30 via-black to-zinc-950 rounded-lg p-4 space-y-3"
        data-testid="users-create-paid-card"
      >
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-red-400" />
          <p className="text-red-400 text-[11px] font-black uppercase tracking-[0.3em]">
            Create paid account · off-site payment
          </p>
        </div>
        <p className="text-zinc-400 text-xs leading-relaxed">
          Use this when someone paid outside Stripe. We&apos;ll create the account, stamp the
          paid tier instantly, and (optionally) email them a magic-link so they can sign in.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-stretch">
          <input
            type="email"
            value={createEmail}
            onChange={(e) => setCreateEmail(e.target.value)}
            placeholder="reader@example.com"
            data-testid="users-create-email"
            className="w-full bg-black border border-zinc-800 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none placeholder:text-zinc-600"
          />
          <select
            value={createPlan}
            onChange={(e) => setCreatePlan(e.target.value)}
            data-testid="users-create-plan"
            className="bg-black border border-zinc-800 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none cursor-pointer"
          >
            <option value="monthly">🤝 Monthly</option>
            <option value="annual">🪙 Annual</option>
            <option value="founding">👑 Founding (lifetime)</option>
          </select>
          <button
            type="button"
            onClick={createPaid}
            disabled={creating || !createEmail.trim()}
            data-testid="users-create-submit"
            className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-black uppercase tracking-[0.25em] px-4 py-2 rounded whitespace-nowrap"
          >
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
            Create + grant access
          </button>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={createSendLink}
            onChange={(e) => setCreateSendLink(e.target.checked)}
            data-testid="users-create-magic-link"
            className="accent-red-600"
          />
          Also email them a magic-link so they can sign in (recommended)
        </label>
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 space-y-3">
        <h3 className="text-xs font-black uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-red-500" /> Closed account usage record
        </h3>
        <p className="text-zinc-400 text-xs leading-relaxed">
          Someone deleted their account and now disputes the charge? Their usage record was kept at
          the moment they closed it. Enter the email to pull the same PDF.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
          <input
            type="email"
            value={closedEmail}
            onChange={(e) => setClosedEmail(e.target.value)}
            placeholder="closed-account@example.com"
            data-testid="users-closed-email"
            className="w-full bg-black border border-zinc-800 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none placeholder:text-zinc-600"
          />
          <button
            type="button"
            onClick={downloadClosedUsage}
            disabled={usageId === 'closed' || !closedEmail.trim()}
            data-testid="users-closed-usage"
            className="inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-[0.25em] px-4 py-2 rounded whitespace-nowrap"
          >
            {usageId === 'closed' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            Get usage PDF
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email…"
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500 rounded pl-8 pr-3 py-2 text-sm text-white outline-none placeholder:text-zinc-600"
            data-testid="users-search"
          />
        </div>
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          data-testid="users-plan-filter"
          className="bg-zinc-950 border border-zinc-800 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
        >
          <option value="">All plans</option>
          <option value="free">Free only</option>
          <option value="monthly">Monthly</option>
          <option value="annual">Annual</option>
          <option value="founding">Founding</option>
          <option value="comp">VIP / Comp</option>
        </select>
      </div>

      {error && <div className="text-red-400 text-sm bg-red-950/30 border border-red-800/50 rounded px-3 py-2" data-testid="users-error">{error}</div>}

      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        {/* Desktop / tablet - table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-zinc-950/80 text-zinc-500 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-left px-4 py-3">Last login</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {loading && (
                <tr><td colSpan="5" className="text-center py-8 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="5" className="text-center py-8 text-zinc-500">No accounts match.</td></tr>
              )}
              {rows.map((u) => (
                <tr key={u.id} className="border-t border-zinc-900 hover:bg-zinc-950/50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 text-white font-mono text-xs sm:text-sm break-all">{u.email}</td>
                  <td className="px-4 py-3">
                    <select
                      value={u.plan || 'free'}
                      onChange={(e) => changePlan(u, e.target.value)}
                      disabled={busyId === u.id}
                      data-testid={`user-plan-select-${u.id}`}
                      title="Manually set tier (for off-site payments - direct debit, cash, contra)"
                      className={`bg-zinc-950 border rounded px-2 py-1 text-[11px] font-black uppercase tracking-widest outline-none cursor-pointer hover:border-red-500 disabled:opacity-50 ${
                        u.plan === 'founding' ? 'text-yellow-300 border-yellow-600/60' :
                        u.plan === 'annual' ? 'text-blue-300 border-blue-700/60' :
                        u.plan === 'monthly' ? 'text-emerald-300 border-emerald-700/60' :
                        'text-zinc-300 border-zinc-700'
                      }`}
                    >
                      <option value="free">Free</option>
                      <option value="monthly">🤝 Monthly</option>
                      <option value="annual">🪙 Annual</option>
                      <option value="founding">👑 Founding</option>
                    </select>
                    {u.comp_access && (
                      <span className="ml-2 inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-yellow-950/70 text-yellow-300 border border-yellow-600/60">
                        🎫 VIP
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmt(u.created_at)}</td>
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmt(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                      <button
                        type="button"
                        onClick={() => toggleComp(u)}
                        disabled={busyId === u.id}
                        data-testid={`user-comp-${u.id}`}
                        className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1.5 rounded ${
                          u.comp_access
                            ? 'bg-red-950/60 text-red-300 border border-red-700/50 hover:bg-red-900/60'
                            : 'bg-emerald-950/60 text-emerald-300 border border-emerald-700/50 hover:bg-emerald-900/60'
                        }`}
                      >
                        {u.comp_access ? <><ShieldOff className="h-3 w-3" /> Revoke VIP</> : <><ShieldCheck className="h-3 w-3" /> Grant VIP</>}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDevicesFor(u)}
                        data-testid={`user-devices-${u.id}`}
                        title="View this account's device history + warning ladder"
                        className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1.5 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300"
                      >
                        <Monitor className="h-3 w-3" /> Devices
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadUsage(u)}
                        disabled={usageId === u.id}
                        data-testid={`user-usage-${u.id}`}
                        title="Download usage record (payment dispute evidence)"
                        className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1.5 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300"
                      >
                        {usageId === u.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <FileText className="h-3 w-3" />} Usage PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => del(u)}
                        disabled={busyId === u.id}
                        data-testid={`user-delete-${u.id}`}
                        className="text-zinc-500 hover:text-red-400 p-1"
                        title="Delete account"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile - stacked cards. Same data as the table above so touch
            targets are 44px+ and nothing overflows on narrow viewports. */}
        <div className="md:hidden divide-y divide-zinc-900" data-testid="users-mobile-list">
          {loading && (
            <div className="text-center py-8 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-center py-8 text-zinc-500">No accounts match.</div>
          )}
          {rows.map((u) => (
            <div key={u.id} className="p-3 space-y-3" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-mono text-[13px] font-black break-all">{u.email}</p>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                    Joined {fmt(u.created_at)}{u.last_login_at && u.last_login_at !== u.created_at ? <> · Last login {fmt(u.last_login_at)}</> : null}
                  </p>
                </div>
                {u.comp_access && (
                  <span className="shrink-0 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-yellow-950/70 text-yellow-300 border border-yellow-600/60">
                    🎫 VIP
                  </span>
                )}
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Plan</label>
                <select
                  value={u.plan || 'free'}
                  onChange={(e) => changePlan(u, e.target.value)}
                  disabled={busyId === u.id}
                  data-testid={`user-plan-select-mobile-${u.id}`}
                  className={`w-full bg-black border rounded px-3 py-2.5 text-sm font-black uppercase tracking-widest outline-none cursor-pointer disabled:opacity-50 ${
                    u.plan === 'founding' ? 'text-yellow-300 border-yellow-600/60' :
                    u.plan === 'annual' ? 'text-blue-300 border-blue-700/60' :
                    u.plan === 'monthly' ? 'text-emerald-300 border-emerald-700/60' :
                    'text-zinc-300 border-zinc-700'
                  }`}
                >
                  <option value="free">Free</option>
                  <option value="monthly">🤝 Monthly</option>
                  <option value="annual">🪙 Annual</option>
                  <option value="founding">👑 Founding</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => toggleComp(u)}
                  disabled={busyId === u.id}
                  data-testid={`user-comp-mobile-${u.id}`}
                  className={`inline-flex items-center justify-center gap-1.5 text-[11px] font-black uppercase tracking-widest px-3 py-2.5 rounded ${
                    u.comp_access
                      ? 'bg-red-950/60 text-red-300 border border-red-700/50'
                      : 'bg-emerald-950/60 text-emerald-300 border border-emerald-700/50'
                  }`}
                >
                  {u.comp_access ? <><ShieldOff className="h-3 w-3" /> Revoke VIP</> : <><ShieldCheck className="h-3 w-3" /> Grant VIP</>}
                </button>
                <button
                  type="button"
                  onClick={() => setDevicesFor(u)}
                  data-testid={`user-devices-mobile-${u.id}`}
                  className="inline-flex items-center justify-center gap-1.5 text-[11px] font-black uppercase tracking-widest px-3 py-2.5 rounded bg-zinc-900 border border-zinc-700 text-zinc-300"
                >
                  <Monitor className="h-3 w-3" /> Devices
                </button>
              </div>
              <button
                type="button"
                onClick={() => downloadUsage(u)}
                disabled={usageId === u.id}
                data-testid={`user-usage-mobile-${u.id}`}
                className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-black uppercase tracking-widest px-3 py-2.5 rounded bg-zinc-900 border border-zinc-700 text-zinc-300"
              >
                {usageId === u.id
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <FileText className="h-3 w-3" />} Usage PDF
              </button>
              <button
                type="button"
                onClick={() => del(u)}
                disabled={busyId === u.id}
                data-testid={`user-delete-mobile-${u.id}`}
                className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-black uppercase tracking-widest px-3 py-2 rounded border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-800"
              >
                <Trash2 className="h-3 w-3" /> Delete account
              </button>
            </div>
          ))}
        </div>
      </div>

      {devicesFor && (
        <UserDevicesModal
          token={token}
          user={devicesFor}
          onClose={() => setDevicesFor(null)}
        />
      )}
    </div>
  );
};

export default UsersTab;
